import React from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import { Grid, Typography, Button, FormHelperText } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import LoginLayout from '../../../features/account/LoginLayout';
import TextField from '../../../components/common/InputField/TextField';
import { setAuthToken } from '../../../utils/cookieUtils';
import { useLoginMutation } from '../../../api/login';
import { getAndClearRedirectUrl } from '../../../utils/sessionTimeout';
import Loader from '../../../components/portals/Loader';

const Login = () => {
    const navigate = useNavigate();
    const [login, { isLoading }] = useLoginMutation();
    const { enqueueSnackbar } = useSnackbar();

    const formik = useFormik({
        initialValues: {
            username: '',
            password: '',
        },
        validationSchema: Yup.object({
            username: Yup.string()
                .transform((value) => value?.trim())
                .max(255)
                .required('Username/Email ID is required'),
            password: Yup.string()
                .transform((value) => value?.trim())
                .max(255)
                .required('Password is required'),
        }),
        onSubmit: async (values, helpers) => {
            try {
                const data = JSON.stringify({
                    username: values.username?.trim(),
                    password: values.password?.trim(),
                });
                const loginResponse = await login(data).unwrap();
                if (!loginResponse) return;
                if (loginResponse.status === 'ERROR') throw new Error(loginResponse.message);
                if (loginResponse.username != values.username?.trim())
                    throw new Error('Invalid Username/Email ID or Password');
                setAuthToken(loginResponse.access_token);
                enqueueSnackbar('Login Successful', {
                    variant: 'success',
                });

                const redirectUrl = getAndClearRedirectUrl();
                navigate(redirectUrl ? redirectUrl : '/');
            } catch (err) {
                helpers.setErrors({
                    submit: 'Invalid Username/Email ID or Password',
                });
                if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
                else
                    enqueueSnackbar(err?.message || 'Something went wrong!', {
                        variant: 'error',
                    });
            }
        },
    });

    return (
        <>
            <LoginLayout>
                {isLoading && <Loader />}
                <Grid container spacing={2} direction="column">
                    <Grid item>
                        <Typography variant="h2" color="textPrimary">
                            Hello Again!
                        </Typography>
                    </Grid>
                    <Grid item>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Username/Email ID"
                            name="username"
                            onChange={formik.handleChange}
                            value={formik.values.username}
                            error={Boolean(formik.touched.username && formik.errors.username)}
                            helperText={formik.touched.username && formik.errors.username}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            fullWidth
                            label="Password"
                            name="password"
                            type="password"
                            onChange={formik.handleChange}
                            value={formik.values.password}
                            error={Boolean(formik.touched.password && formik.errors.password)}
                            helperText={formik.touched.password && formik.errors.password}
                        />
                    </Grid>
                    {formik.errors.submit && (
                        <Grid item>
                            <FormHelperText error>{formik.errors.submit}</FormHelperText>
                        </Grid>
                    )}
                    <Grid item alignSelf={'flex-end'}>
                        <Button
                            variant="text"
                            sx={{
                                color: 'secondary.main',
                            }}
                            onClick={() => navigate('/forgot-password')}
                        >
                            Forgot Password?
                        </Button>
                    </Grid>
                    <Grid item>
                        <LoadingButton variant="contained" color="primary" fullWidth onClick={formik.handleSubmit}>
                            Login
                        </LoadingButton>
                    </Grid>
                </Grid>
            </LoginLayout>
        </>
    );
};

export default Login;
